export type SystemConstantScope = 'ENVIRONMENT' | 'SEND';

export interface SystemConstant {
    name: string;
    label: string;
    scope: SystemConstantScope;
    description: string;
    example: string;
    valueByEnvironment: Record<string, string>;
}

export const SYSTEM_CONSTANT_SCOPE_LABELS: Record<SystemConstantScope, string> = {
    ENVIRONMENT: 'Do ambiente',
    SEND: 'Do envio',
};

const sameInEveryEnvironment = (value: string): Record<string, string> => ({
    prd: value,
    hml: value,
    dev: value,
    sandbox: value,
});

export const SYSTEM_CONSTANTS: SystemConstant[] = [
    {
        name: 'APP_BO_ADDRESS',
        label: 'Endereço do backoffice',
        scope: 'ENVIRONMENT',
        description: 'URL do backoffice do ambiente onde a mensagem é enviada. Use para montar links para telas do sistema.',
        example: '{{APP_BO_ADDRESS}}/collateral/credit-right-batches/{{batchId}}',
        valueByEnvironment: {
            prd: 'https://bo2.agrolend.agr.br',
            hml: 'https://bo2.agrolend-hml.agrolend.dev.br',
            dev: 'https://bo2.agrolend-dev.agrolend.dev.br',
            sandbox: 'https://bo2.sandbox.agrolend.dev.br',
        },
    },
    {
        name: 'APP_ENVIRONMENT',
        label: 'Ambiente',
        scope: 'ENVIRONMENT',
        description: 'Ambiente onde a mensagem é enviada (prd, hml, dev, sandbox). Use para marcar mensagens de teste.',
        example: "{% if APP_ENVIRONMENT != 'prd' %}[TESTE] {% endif %}",
        valueByEnvironment: { prd: 'prd', hml: 'hml', dev: 'dev', sandbox: 'sandbox' },
    },
    {
        name: 'APP_SENT_AT',
        label: 'Data e hora do envio',
        scope: 'SEND',
        description: 'Momento em que a mensagem foi gerada, no fuso de São Paulo (dd/mm/aaaa hh:mm).',
        example: 'Enviado em {{APP_SENT_AT}}',
        valueByEnvironment: sameInEveryEnvironment('15/10/2026 09:30'),
    },
    {
        name: 'APP_EVENT_NAME',
        label: 'Evento que disparou',
        scope: 'SEND',
        description: 'Evento de domínio que acionou o roteamento (vazio em envio manual). Ajuda a entender a origem de um alerta.',
        example: 'Origem: {{APP_EVENT_NAME}}',
        valueByEnvironment: sameInEveryEnvironment('INVOICE_DUE_SOON'),
    },
    {
        name: 'APP_CORRELATION_ID',
        label: 'Id de correlação',
        scope: 'SEND',
        description: 'Id que acompanha o evento entre os serviços. Use em alertas para rastrear a mensagem nos logs.',
        example: 'Rastreio: {{APP_CORRELATION_ID}}',
        valueByEnvironment: sameInEveryEnvironment('9f1c2b7e-4a1d-4c3e-8b2f-6d5e4a3b2c1d'),
    },
];

const CONSTANT_NAMES = new Set(SYSTEM_CONSTANTS.map((c) => c.name));

export const isSystemConstant = (name: string): boolean => CONSTANT_NAMES.has(name);

export const constantValues = (environment = 'prd'): Record<string, string> =>
    Object.fromEntries(SYSTEM_CONSTANTS.map((c) => [c.name, c.valueByEnvironment[environment] ?? c.valueByEnvironment.prd]));
